import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories } from "@/mocks/categories";
import { pillarArticleList } from "@/mocks/pillar-articles";

const featuredSections = [
  {
    title: "featured.spreadsheet.title",
    desc: "featured.spreadsheet.desc",
    cta: "featured.spreadsheet.cta",
    link: "/cssbuy-spreadsheet/",
    icon: "ri-table-line",
    bg: "bg-primary-50/50",
    iconColor: "text-primary-500",
  },
  {
    title: "featured.review.title",
    desc: "featured.review.desc",
    cta: "featured.review.cta",
    link: "/cssbuy-review/",
    icon: "ri-article-line",
    bg: "bg-secondary-50/50",
    iconColor: "text-secondary-500",
  },
  {
    title: "featured.shipping.title",
    desc: "featured.shipping.desc",
    cta: "featured.shipping.cta",
    link: "/cssbuy-shipping/",
    icon: "ri-truck-line",
    bg: "bg-accent-50/50",
    iconColor: "text-accent-500",
  },
  {
    title: "featured.coupon.title",
    desc: "featured.coupon.desc",
    cta: "featured.coupon.cta",
    link: "/cssbuy-coupon/",
    icon: "ri-coupon-line",
    bg: "bg-primary-50/50",
    iconColor: "text-primary-500",
  },
  {
    title: "featured.warehouse.title",
    desc: "featured.warehouse.desc",
    cta: "featured.warehouse.cta",
    link: "/cssbuy-warehouse/",
    icon: "ri-store-3-line",
    bg: "bg-secondary-50/50",
    iconColor: "text-secondary-500",
  },
  {
    title: "featured.safety.title",
    desc: "featured.safety.desc",
    cta: "featured.safety.cta",
    link: "/cssbuy-safety/",
    icon: "ri-shield-check-line",
    bg: "bg-accent-50/50",
    iconColor: "text-accent-500",
  },
];

const faqItems = [
  { q: "faq.q1", a: "faq.a1" },
  { q: "faq.q2", a: "faq.a2" },
  { q: "faq.q3", a: "faq.a3" },
  { q: "faq.q4", a: "faq.a4" },
  { q: "faq.q5", a: "faq.a5" },
  { q: "faq.q6", a: "faq.a6" },
  { q: "faq.q7", a: "faq.a7" },
  { q: "faq.q8", a: "faq.a8" },
];

export default function Home() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language as "en" | "pt-BR" | "de") || "en";
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleNewsletter = (e: FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setError(t("newsletter.error"));
      return;
    }
    setError("");

    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);

    const honeypot = formData.get("company_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setSubmitted(true);
      setEmail("");
      return;
    }

    formData.delete("company_alt");

    fetch("https://readdy.ai/api/form/daa0ur9tacug2b80t020", {
      method: "POST",
      body: formData,
    })
      .then(async (res) => {
        const responseText = await res.text();
        let parsed;
        try {
          parsed = JSON.parse(responseText);
        } catch {
          parsed = null;
        }
        if (res.ok && parsed?.code === "OK") {
          setSubmitted(true);
          setEmail("");
        } else {
          setError("Something went wrong. Please try again.");
        }
      })
      .catch(() => {
        setError("Something went wrong. Please try again.");
      });
  };

  const trustPoints = [
    "hero.trust1",
    "hero.trust2",
    "hero.trust3",
    "hero.trust4",
    "hero.trust5",
    "hero.trust6",
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20curated%20flat%20lay%20of%20colorful%20fashion%20products%20sneakers%20hoodies%20bags%20and%20accessories%20arranged%20artistically%20on%20soft%20pastel%20background%20warm%20natural%20lighting%20editorial%20ecommerce%20photography%20fresh%20modern%20aesthetic&width=1600&height=900&seq=hero-2026-cssbuy-1&orientation=landscape&nocache=true"
            alt="CSSBuy Spreadsheet products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/80 to-background-50/60" />
        </div>

        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 py-16 md:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
                <i className="ri-check-line" />
                <span>CSSBuy Spreadsheet 2026</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
                {t("hero.h1")}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-xl">
                {t("hero.subtitle")}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  {t("hero.cta.primary")}
                  <i className="ri-arrow-right-line" />
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-50 border border-background-200 text-foreground-700 font-medium rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  {t("hero.cta.secondary")}
                </a>
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-2 pt-2">
                {trustPoints.map((tp) => (
                  <span key={tp} className="flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-check-line text-primary-500" />
                    {t(tp)}
                  </span>
                ))}
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="bg-background-50/90 backdrop-blur-sm border border-background-200/60 rounded-xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-foreground-800">CSSBuy Spreadsheet Preview</h3>
                  <span className="text-xs text-foreground-400 bg-background-100 px-2 py-1 rounded">Updated Aug 2026</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-background-200/60">
                        <th className="text-left py-2 px-2 text-foreground-500 font-medium">Product</th>
                        <th className="text-left py-2 px-2 text-foreground-500 font-medium">Category</th>
                        <th className="text-left py-2 px-2 text-foreground-500 font-medium">Seller</th>
                        <th className="text-left py-2 px-2 text-foreground-500 font-medium">QC</th>
                        <th className="text-left py-2 px-2 text-foreground-500 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="text-foreground-700">
                      <tr className="border-b border-background-100">
                        <td className="py-2 px-2">Sneaker Model X</td>
                        <td className="py-2 px-2"><span className="bg-primary-50 text-primary-700 px-1.5 py-0.5 rounded text-[10px]">Shoes</span></td>
                        <td className="py-2 px-2">TopSeller99</td>
                        <td className="py-2 px-2"><i className="ri-check-line text-primary-500" /></td>
                        <td className="py-2 px-2"><span className="text-primary-600">Available</span></td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-2 px-2">Street Hoodie V2</td>
                        <td className="py-2 px-2"><span className="bg-accent-50 text-accent-700 px-1.5 py-0.5 rounded text-[10px]">Hoodies</span></td>
                        <td className="py-2 px-2">HoodieKing</td>
                        <td className="py-2 px-2"><i className="ri-check-line text-primary-500" /></td>
                        <td className="py-2 px-2"><span className="text-primary-600">Available</span></td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-2 px-2">Designer Bag L</td>
                        <td className="py-2 px-2"><span className="bg-secondary-50 text-secondary-700 px-1.5 py-0.5 rounded text-[10px]">Bags</span></td>
                        <td className="py-2 px-2">BagPro</td>
                        <td className="py-2 px-2"><i className="ri-time-line text-foreground-400" /></td>
                        <td className="py-2 px-2"><span className="text-foreground-500">Pending</span></td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2">Sport Jersey 2026</td>
                        <td className="py-2 px-2"><span className="bg-primary-50 text-primary-700 px-1.5 py-0.5 rounded text-[10px]">Jersey</span></td>
                        <td className="py-2 px-2">JerseyShop</td>
                        <td className="py-2 px-2"><i className="ri-check-line text-primary-500" /></td>
                        <td className="py-2 px-2"><span className="text-primary-600">Available</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 text-center">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
                  >
                    View Full Spreadsheet <i className="ri-arrow-right-line" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-16 md:py-20 bg-background-100/50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t("categories.title")}
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto">
              {t("categories.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-4">
            {categories.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group flex flex-col gap-2.5 p-3 md:p-4 bg-background-50 border border-background-200/60 rounded-xl hover:border-primary-300 transition-all"
              >
                <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={cat.image}
                    alt={t(cat.key)}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <span className="text-xs md:text-sm font-medium text-foreground-700 text-center group-hover:text-primary-600 transition-colors leading-snug">
                  {t(cat.key)}
                </span>
              </a>
            ))}
          </div>
          <div className="text-center mt-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              {t("categories.cta")}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </section>

      {/* Featured Sections */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {featuredSections.map((section) => (
              <div
                key={section.title}
                className={`${section.bg} border border-background-200/60 rounded-xl p-5 md:p-6 hover:border-primary-300 transition-colors`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${section.bg} ${section.iconColor} mb-4`}>
                  <i className={`${section.icon} text-xl`} />
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">
                  {t(section.title)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {t(section.desc)}
                </p>
                <Link
                  to={section.link}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  {t(section.cta)}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillar Articles */}
      <section className="py-16 md:py-20 bg-background-100/50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t("articles.title")}
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto">
              {t("articles.subtitle")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {pillarArticleList.map((article) => {
              const content = article[lang] || article.en;
              return (
                <Link
                  key={article.path}
                  to={article.path}
                  className="group flex flex-col bg-background-50 border border-background-200/60 rounded-xl overflow-hidden hover:border-primary-300 transition-all"
                >
                  <div className="relative w-full aspect-[3/2] overflow-hidden bg-background-100">
                    <img
                      src={article.image}
                      alt={content.h1}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 leading-snug group-hover:text-primary-700 transition-colors">
                      {content.h1}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4 flex-1">
                      {content.summary}
                    </p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors">
                      {t("articles.readMore")}
                      <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t("faq.title")}
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqItems.map((item, idx) => (
              <div
                key={idx}
                className="bg-background-50 border border-background-200/60 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 md:p-5 text-left"
                >
                  <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                    {t(item.q)}
                  </span>
                  <i
                    className={`ri-${openFaq === idx ? "subtract" : "add"}-line text-foreground-400 shrink-0 transition-transform`}
                  />
                </button>
                {openFaq === idx && (
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {t(item.a)}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 md:py-20 bg-background-100/50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mx-auto mb-4">
              <i className="ri-mail-line text-xl" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t("newsletter.title")}
            </h2>
            <p className="text-foreground-600 mb-6">
              {t("newsletter.subtitle")}
            </p>
            {!submitted ? (
              <form
                onSubmit={handleNewsletter}
                data-readdy-form
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("newsletter.placeholder")}
                  className="flex-1 px-4 py-3 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hidden"
                  value=""
                  onChange={() => {}}
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  {t("newsletter.button")}
                </button>
              </form>
            ) : (
              <div className="flex items-center justify-center gap-2 text-primary-600">
                <i className="ri-check-line text-xl" />
                <span className="font-medium">{t("newsletter.success")}</span>
              </div>
            )}
            {error && (
              <p className="text-accent-500 text-sm mt-3">{error}</p>
            )}
            <p className="text-xs text-foreground-400 mt-4">
              {t("newsletter.privacy")}
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-primary-50/50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {t("cta.main.title")}
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto mb-6">
            {t("cta.main.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t("cta.main.primary")}
              <i className="ri-arrow-right-line" />
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-50 border border-background-200 text-foreground-700 font-medium rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              {t("cta.main.secondary")}
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer Bar */}
      <div className="bg-background-100 border-t border-background-200/60 py-4">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
          <p className="text-xs text-foreground-500">
            {t("disclaimer")}
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}